import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'data-weather',
    category: 'data',
    title: 'Wetterdateien',
    summary: 'Optionale Dateien, die Solar- und Windprognosen genauer machen.',
    body: (
      <>
        <p>
          Wetterdateien sind optional, verbessern jedoch die Prognosegenauigkeit. Es gibt zwei Arten.
        </p>
        <h5 className="mt-3">Solarwetter</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Spalte</th><th>Erforderlich?</th><th>Bedeutung</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Erforderlich</td><td>Die Stunde</td></tr>
            <tr><td><Code>ghi_w_m2</Code></td><td>Erforderlich</td><td>Sonnenlichtintensität in Watt pro Quadratmeter</td></tr>
            <tr><td><Code>temperature_c</Code></td><td>Optional</td><td>Lufttemperatur, °C</td></tr>
            <tr><td><Code>cloud_cover_pct</Code></td><td>Optional</td><td>Bewölkungsgrad, 0–100 %</td></tr>
            <tr><td><Code>dni_w_m2</Code></td><td>Optional</td><td>Direkte Sonnenstrahlung, W/m²</td></tr>
          </tbody>
        </Table>
        <TemplateBtn type="weather_solar">Solarwetter-Vorlage herunterladen</TemplateBtn>

        <h5 className="mt-4">Windwetter</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Spalte</th><th>Erforderlich?</th><th>Bedeutung</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Erforderlich</td><td>Die Stunde</td></tr>
            <tr><td><Code>wind_speed_m_s</Code></td><td>Erforderlich</td><td>Windgeschwindigkeit in Metern pro Sekunde</td></tr>
            <tr><td><Code>wind_direction_deg</Code></td><td>Optional</td><td>Windrichtung, 0–360°</td></tr>
            <tr><td><Code>temperature_c</Code></td><td>Optional</td><td>Lufttemperatur, °C</td></tr>
          </tbody>
        </Table>
        <TemplateBtn type="weather_wind">Windwetter-Vorlage herunterladen</TemplateBtn>
        <Tip>Optionale Spalten können vollständig weggelassen werden — wenn Sie eine Spalte jedoch einschließen, füllen Sie sie für jede Zeile aus.</Tip>

        <h5 className="mt-4">Wo bekomme ich Wetterdaten?</h5>
        <p>
          In den meisten Fällen müssen Sie diese Daten nicht selbst aufzeichnen. Mehrere kostenlose
          Dienste veröffentlichen stündliche historische Wetterdaten für jeden Standort in Europa.
          Beginnen Sie mit den universellen Diensten — sie decken alle Pilotländer ab und erfordern
          kein spezielles Konto.
        </p>

        <h6 className="mt-3">Open-Meteo (empfohlen, am einfachsten)</h6>
        <p>
          <Ext href="https://open-meteo.com/en/docs/historical-weather-api">open-meteo.com/en/docs/historical-weather-api</Ext>
          {' '}— kostenlos, kein Konto erforderlich, stündlicher Verlauf bis 1940.
        </p>
        <ol>
          <li>Öffnen Sie die obige Seite. Geben Sie im Formular den <strong>Breiten- und
            Längengrad</strong> Ihrer Gemeinschaft ein (zu finden auf Google Maps — rechtsklick
            auf den Standort → „Was ist hier?").</li>
          <li>Legen Sie <strong>Startdatum</strong> und <strong>Enddatum</strong> für das gewünschte Jahr fest.</li>
          <li>Wählen Sie unter <strong>Stündliche Variablen</strong> die folgenden aus (überspringen
            Sie die Windliste, wenn Sie nur Solardaten benötigen, und umgekehrt):
            <ul>
              <li><Code>shortwave_radiation</Code> — das ist GHI (in W/m²).</li>
              <li><Code>direct_radiation</Code> — das ist DNI (optional).</li>
              <li><Code>temperature_2m</Code> — Lufttemperatur (°C).</li>
              <li><Code>cloud_cover</Code> — Bewölkungsgrad (%).</li>
              <li><Code>wind_speed_10m</Code> — Windgeschwindigkeit in 10 m Höhe (m/s).</li>
              <li><Code>wind_direction_10m</Code> — Windrichtung (Grad).</li>
            </ul>
          </li>
          <li>Stellen Sie das Format auf <strong>CSV</strong> ein und klicken Sie auf <strong>Herunterladen</strong>.</li>
          <li>Öffnen Sie die Datei. Benennen Sie die Spalten in die von der Plattform erwarteten Namen um:
            {' '}<Code>shortwave_radiation</Code> → <Code>ghi_w_m2</Code>,
            {' '}<Code>direct_radiation</Code> → <Code>dni_w_m2</Code>,
            {' '}<Code>temperature_2m</Code> → <Code>temperature_c</Code>,
            {' '}<Code>cloud_cover</Code> → <Code>cloud_cover_pct</Code>,
            {' '}<Code>wind_speed_10m</Code> → <Code>wind_speed_m_s</Code>,
            {' '}<Code>wind_direction_10m</Code> → <Code>wind_direction_deg</Code>.</li>
          <li>Stellen Sie sicher, dass die Zeitstempelspalte <Code>timestamp</Code> heißt. Speichern Sie als CSV UTF-8 und
            laden Sie die Datei hoch.</li>
        </ol>

        <h6 className="mt-3">PVGIS — Gemeinsame Forschungsstelle der Europäischen Kommission (am besten für Solarplanung)</h6>
        <p>
          <Ext href="https://re.jrc.ec.europa.eu/pvg_tools/en/">re.jrc.ec.europa.eu/pvg_tools/en/</Ext>
          {' '}— kostenlos, kein Konto, deckt ganz Europa ab. Besonders nützlich, wenn Sie ein
          „typisches Jahr" als Referenz (TMY) benötigen und kein bestimmtes historisches Jahr.
        </p>
        <ol>
          <li>Öffnen Sie die Website und wählen Sie Ihren Standort auf der Karte.</li>
          <li>Wählen Sie in den linken Reitern <strong>„Stündliche Daten"</strong>.</li>
          <li>Aktivieren Sie <strong>Solarstrahlungskomponenten</strong> und <strong>Lufttemperatur 2 m</strong>,
            wählen Sie ein Jahr (oder einen Bereich) und klicken Sie auf <strong>CSV herunterladen</strong>.</li>
          <li>Die heruntergeladene Datei enthält einen kleinen Kopfbereich, den Sie möglicherweise
            löschen müssen, damit die Spaltenzeile
            (<Code>time</Code>, <Code>G(i)</Code>, <Code>T2m</Code>, …) in Zeile 1 steht. Benennen Sie
            {' '}<Code>G(i)</Code> → <Code>ghi_w_m2</Code> und <Code>T2m</Code> → <Code>temperature_c</Code> um.</li>
        </ol>
        <Tip>PVGIS-Zeiten sind in der Regel UTC, und Zeitstempel können wie <Code>20250101:1000</Code> aussehen
          — konvertieren Sie sie mit der Formel aus
          {' '}<ArticleLink to="data-csv-basics">CSV-Dateien korrekt erstellen</ArticleLink> in das Format <Code>2025-01-01T10:00:00Z</Code>.</Tip>

        <h6 className="mt-3">Renewables.ninja (standortspezifische Solar-/Windleistung)</h6>
        <p>
          <Ext href="https://www.renewables.ninja">renewables.ninja</Ext> — kostenlos mit kurzer
          Registrierung. Koordinaten eingeben, „MERRA-2" oder „ERA-5", Jahr und Auflösung wählen
          und CSV herunterladen. Die zuzuordnende Variable ist <Code>irradiance</Code> → <Code>ghi_w_m2</Code> für
          Solar und <Code>wind_speed</Code> → <Code>wind_speed_m_s</Code> für Wind.
        </p>

        <h6 className="mt-3">NASA POWER (globale Ausweichoption)</h6>
        <p>
          <Ext href="https://power.larc.nasa.gov/data-access-viewer/">power.larc.nasa.gov/data-access-viewer</Ext>
          {' '}— globale Stundendaten, kostenlos. Nützlich, wenn andere Quellen den Standort nicht
          abdecken. Variablennamen: <Code>ALLSKY_SFC_SW_DWN</Code> → <Code>ghi_w_m2</Code>,
          {' '}<Code>T2M</Code> → <Code>temperature_c</Code>,
          {' '}<Code>WS10M</Code> → <Code>wind_speed_m_s</Code>.
        </p>

        <h6 className="mt-3">Nationale Wetterdienste</h6>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Land</th><th>Dienst</th></tr>
          </thead>
          <tbody>
            <tr><td>Italien</td><td>ISPRA / regionale ARPA (z. B. ARPA Lombardia, ARPA Veneto) — stündliche Stationsdaten je Region.</td></tr>
            <tr><td>Frankreich</td><td>Météo-France Open-Data-Portal (<Ext href="https://meteo.data.gouv.fr">meteo.data.gouv.fr</Ext>) — historische Stundenbeobachtungen.</td></tr>
            <tr><td>Deutschland</td><td>DWD Climate Data Center (<Ext href="https://opendata.dwd.de">opendata.dwd.de</Ext>) — kostenlos, stündliche Stationsdaten.</td></tr>
            <tr><td>Tschechien</td><td>CHMI (<Ext href="https://www.chmi.cz">chmi.cz</Ext>) — historische Stationsdaten auf Anfrage / über das Portal.</td></tr>
            <tr><td>Griechenland</td><td>HNMS / NOA-Meteo (<Ext href="https://www.meteo.gr">meteo.gr</Ext>) — begrenzte kostenlose historische Daten; Open-Meteo ist in der Regel einfacher.</td></tr>
          </tbody>
        </Table>

        <Warn>
          Stimmen Sie das <strong>Jahr</strong> und die <strong>Zeitzone</strong> Ihrer Wetterdatei
          mit Ihren anderen Dateien ab. Wenn Ihre Lastdaten für 2024 in Ortszeit vorliegen, laden Sie
          die Wetterdaten für 2024 in UTC herunter und konvertieren Sie sie (Open-Meteo ermöglicht die
          Auswahl der Zeitzone vor dem Herunterladen). Nicht übereinstimmende Jahre oder Zeitzonen
          verschlechtern die Prognosen, anstatt sie zu verbessern.
        </Warn>
      </>
    ),
  },

  {
    id: 'data-bill',
    category: 'data',
    title: 'Energierechnungsdateien',
    summary: 'Monatliche Abrechnungsdaten für die Kostenanalyse.',
    body: (
      <>
        <p>
          Eine <strong>Energierechnungs</strong>datei listet vergangene Rechnungen auf, eine Zeile
          pro Abrechnungszeitraum (in der Regel ein Monat). Sie wird für die Kostenanalyse verwendet.
        </p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Spalte</th><th>Erforderlich?</th><th>Bedeutung</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>period_start</Code></td><td>Erforderlich</td><td>Erster Tag der Rechnung, z. B. <Code>2025-01-01</Code></td></tr>
            <tr><td><Code>period_end</Code></td><td>Erforderlich</td><td>Letzter Tag der Rechnung, z. B. <Code>2025-01-31</Code></td></tr>
            <tr><td><Code>consumption_kwh</Code></td><td>Erforderlich</td><td>Gesamter Stromverbrauch im Zeitraum, in kWh</td></tr>
            <tr><td><Code>cost_eur</Code></td><td>Erforderlich</td><td>Gesamtbetrag der Zahlung, in Euro</td></tr>
            <tr><td><Code>peak_demand_kw</Code></td><td>Optional</td><td>Höchster Leistungsbedarf im Zeitraum, in kW</td></tr>
            <tr><td><Code>rate_eur_per_kwh</Code></td><td>Optional</td><td>Durchschnittspreis pro kWh</td></tr>
          </tbody>
        </Table>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`period_start,period_end,consumption_kwh,cost_eur
2025-01-01,2025-01-31,3850,962.50
2025-02-01,2025-02-28,3520,880.00`}
        </pre>
        <TemplateBtn type="building_energy_bill">Energierechnungsvorlage herunterladen</TemplateBtn>
        <Warn>Das Startdatum jedes Zeitraums muss vor dem Enddatum liegen, und die Zeiträume dürfen sich nicht überschneiden.</Warn>
      </>
    ),
  },
]
